import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  useServerPreviewDriver,
  useWebsiteChatSubmit,
} from "@hooks/website";
import { StepProgress } from "@components/ui/step-progress";
import {
  PreviewStateStore,
  usePreviewState,
  usePreviewUI,
  useConsoleErrors,
  type UIMode,
} from "@lib/preview";
import { useProjectUuid } from "@stores/projectStore";
import { usePreviewRoute, usePreviewStore } from "@stores/previewStore";
import { analytics } from "@lib/analytics";
import { useRotatingQuote } from "~/hooks/useRotatingQuote";

const loadingStepDescriptors = [
  { id: "booting", label: "Starting preview environment..." },
  { id: "mounting", label: "Loading files..." },
  { id: "installing", label: "Installing dependencies..." },
  { id: "starting", label: "Starting preview server..." },
];

/**
 * Map a derived loading step to the index in loadingStepDescriptors.
 * `idle` + `booting` → 0, `rendering`/`agent-working` → stay at final step so
 * the loader doesn't visually reset while we wait for the iframe.
 */
function loadingStepIndex(step: Exclude<UIMode, { mode: "iframe" } | { mode: "error" }>["step"]): number {
  switch (step) {
    case "idle":
    case "booting":
      return 0;
    case "mounting":
      return 1;
    case "installing":
      return 2;
    case "starting":
      return 3;
    case "agent-working":
    case "rendering":
      return 3;
  }
}

function LoadingOverlay({ step }: { step: Exclude<UIMode, { mode: "iframe" } | { mode: "error" }>["step"] }) {
  const funQuote = useRotatingQuote("build");
  return (
    <div data-testid="preview-status" data-status={step}>
      <StepProgress
        title="Loading your preview"
        steps={loadingStepDescriptors}
        currentStep={loadingStepIndex(step)}
        funQuote={funQuote}
      />
    </div>
  );
}

function RedCircleIcon() {
  return (
    <div className="size-10 rounded-full bg-red-100 flex items-center justify-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-red-500"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" x2="12" y1="8" y2="12" />
        <line x1="12" x2="12.01" y1="16" y2="16" />
      </svg>
    </div>
  );
}

function BuildErrorCard({ onFix }: { onFix: () => void }) {
  return (
    <div
      data-testid="preview-status"
      data-status="build-error"
      className="flex flex-col items-center gap-3 text-center px-8"
    >
      <RedCircleIcon />
      <p className="text-sm font-medium text-neutral-700">We had an issue building your page</p>
      <button
        onClick={onFix}
        className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
      >
        Fix errors
      </button>
    </div>
  );
}

function GenerationFailedCard({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      data-testid="preview-status"
      data-status="generation-failed"
      className="flex flex-col items-center gap-3 text-center px-8"
    >
      <RedCircleIcon />
      <p className="text-sm font-medium text-neutral-700">We had an issue generating your page</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
      >
        Try again
      </button>
    </div>
  );
}

function RenderTimeoutCard({ onFix }: { onFix: () => void }) {
  return (
    <div
      data-testid="preview-status"
      data-status="render-timeout"
      className="flex flex-col items-center gap-3 text-center px-8"
    >
      <RedCircleIcon />
      <p className="text-sm font-medium text-neutral-700">
        The preview didn&apos;t render in time
      </p>
      <button
        onClick={onFix}
        className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
      >
        Fix errors
      </button>
    </div>
  );
}

function ContainerErrorCard({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      data-testid="preview-status"
      data-status="container-error"
      className="flex flex-col items-center justify-center gap-4 p-8"
    >
      <div className="text-red-500 text-4xl">!</div>
      <p className="text-red-600 text-sm text-center max-w-md">{message}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 text-sm bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
      >
        Retry
      </button>
    </div>
  );
}

/**
 * Preview component: renders the preview iframe, with an overlay that
 * appears in loading and error states. All UI state lives in
 * `PreviewStateStore`; this component is ~a pure projection of `deriveUI`.
 */
export function WebsitePreview() {
  useServerPreviewDriver();
  const ui = usePreviewUI();
  const state = usePreviewState();
  const submit = useWebsiteChatSubmit();
  const projectUuid = useProjectUuid();
  const buildErrors = useConsoleErrors();
  const activeRoute = usePreviewRoute();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hasTrackedPreview = useRef(false);

  // The src we want the iframe to be at = previewUrl + active route. Used as
  // the initial src on mount; subsequent route changes are driven imperatively
  // (see effect below) so they don't trigger a full remount.
  const displayUrl = useMemo(() => {
    if (!state.previewUrl) return state.previewUrl;
    const route = activeRoute ?? "/";
    const base = state.previewUrl.replace(/\/$/, "");
    return route === "/" ? `${base}/` : `${base}${route}`;
  }, [state.previewUrl, activeRoute]);

  // Drive iframe navigation imperatively. The store tags every route write
  // with its source ('picker' vs 'iframe'); we only navigate when the latest
  // write came from the picker. Iframe-initiated changes mean the iframe is
  // already at that route, so re-navigating would be a wasteful reload.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !displayUrl) return;
    if (usePreviewStore.getState().routeSource !== "picker") return;
    try {
      const win = iframe.contentWindow;
      if (win) {
        win.location.replace(displayUrl);
      } else {
        iframe.src = displayUrl;
      }
    } catch {
      iframe.src = displayUrl;
    }
  }, [displayUrl]);

  // Reset the "already tracked" latch when projects switch.
  useEffect(() => {
    hasTrackedPreview.current = false;
  }, [projectUuid]);

  // Listen for postMessage from the preview iframe.
  // - "preview-ready" signals content has rendered (handed to the state store).
  // - "preview-state" carries patches from the injected client (route changes
  //   today; future fields like scroll/viewport land here without protocol changes).
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "preview-ready") {
        if (import.meta.env?.DEV) {
          // Which route's render fired this ready signal? If it differs from
          // a route that just SSR-threw, this is the healthy-sibling clobber.
          // eslint-disable-next-line no-console
          console.warn(
            "[SSR-DEBUG] preview-ready received; store route=%s defaultTemplate=%s",
            usePreviewStore.getState().route ?? "/",
            !!event.data.defaultTemplate,
          );
        }
        // The injected preview-client (when present) gives us the real
        // defaultTemplate verdict; the analytics "previewed" event fires from
        // the universal iframe `load` signal (handleIframeLoad), not here, so a
        // tag-less server build still tracks exactly once.
        PreviewStateStore.iframeRendered({
          defaultTemplate: !!event.data.defaultTemplate,
        });
        return;
      }
      if (event.data?.type === "preview-state") {
        const patch = event.data.patch;
        if (patch && typeof patch === "object") {
          usePreviewStore.getState().applyPatch(patch);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [projectUuid]);

  const handleFixErrors = useCallback(() => {
    // Bypasses useWebsiteComposer's formatSubmitWithActivePage (raw stream
    // submit), so attach the active page route here too — otherwise the
    // ActivePageFocus reminder defaults to `/` and aims the agent at the index
    // page instead of the page the user is looking at where the error occurred.
    const activePageRoute = usePreviewStore.getState().route;
    submit({
      messages: [
        { role: "human", content: "My page isn't displaying correctly, can you fix it?" },
      ],
      consoleErrors: buildErrors,
      ...(activePageRoute ? { activePageRoute } : {}),
    } as any);
    if (projectUuid) {
      analytics.trackProject("website_edited", projectUuid, { edit_type: "error_fix" });
    }
  }, [buildErrors, submit, projectUuid]);

  const handleRetryGeneration = useCallback(() => {
    submit({
      messages: [{ role: "human", content: "My page isn't displaying correctly, can you fix it?" }],
    } as any);
    if (projectUuid) {
      analytics.trackProject("website_edited", projectUuid, { edit_type: "generation_retry" });
    }
  }, [submit, projectUuid]);

  // The framed document's `load` event is the universal "content is on screen"
  // signal: the static server build injects no preview-client to post
  // `preview-ready`, so this drives both the render→rendered transition (lifts
  // the loading overlay, no white flash) and the once-per-project
  // `website_previewed` analytics event. The latch survives iframe remounts
  // (every build bumps iframeKey → a fresh `load`) and resets on project switch.
  const handleIframeLoad = useCallback(() => {
    PreviewStateStore.iframeRendered({ defaultTemplate: false });
    if (projectUuid && !hasTrackedPreview.current) {
      hasTrackedPreview.current = true;
      analytics.trackProject("website_previewed", projectUuid);
    }
  }, [projectUuid]);

  const handleContainerRetry = useCallback(() => {
    // A page refresh is the only reliable way to re-attempt loading the preview.
    if (typeof window !== "undefined") window.location.reload();
  }, []);

  const overlay = renderOverlay(ui, {
    onFix: handleFixErrors,
    onRetryGeneration: handleRetryGeneration,
    onContainerRetry: handleContainerRetry,
  });

  // Gate on iframeKey > 0 so we never render an iframe before the first
  // post-mount remount. Before that point the on-disk files reflect either
  // the snapshot template or the prior project; the overlay covers the
  // gap. Today the overlay covers via z-index — unmounting the iframe is
  // belt-and-suspenders against any future layering or transparency change.
  const showIframe =
    state.container === "ready" && state.previewUrl !== null && state.iframeKey > 0;

  return (
    <div
      className="w-full h-full flex flex-col rounded-2xl border border-neutral-200 overflow-hidden"
      data-testid="preview-container"
    >
      <div className="flex-1 relative bg-white">
        {showIframe && (
          <iframe
            ref={iframeRef}
            key={state.iframeKey}
            src={displayUrl ?? state.previewUrl!}
            // See handleIframeLoad: the framed document's load event drives the
            // render→rendered transition (lifts the overlay) and the
            // once-per-project website_previewed analytics event.
            onLoad={handleIframeLoad}
            className="w-full h-full border-none"
            title="Website Preview"
            data-testid="preview-iframe"
            // allow-downloads: lead-magnet links use Content-Disposition:
            // attachment; a popup opened from this iframe inherits these sandbox
            // flags, so without this the download is silently blocked in-preview
            // even though it works on the real deploy.
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
          />
        )}

        {overlay && (
          <div
            data-testid="preview-overlay"
            className="absolute inset-0 z-10 flex items-center justify-center bg-neutral-50"
          >
            {overlay}
          </div>
        )}
      </div>
    </div>
  );
}

function renderOverlay(
  ui: UIMode,
  handlers: {
    onFix: () => void;
    onRetryGeneration: () => void;
    onContainerRetry: () => void;
  },
): React.ReactNode {
  switch (ui.mode) {
    case "iframe":
      return null;
    case "loading":
      return <LoadingOverlay step={ui.step} />;
    case "error":
      switch (ui.kind) {
        case "container":
          return (
            <ContainerErrorCard
              message={ui.message ?? "Unknown error"}
              onRetry={handlers.onContainerRetry}
            />
          );
        case "build":
          return <BuildErrorCard onFix={handlers.onFix} />;
        case "render":
          return <RenderTimeoutCard onFix={handlers.onFix} />;
        case "generation-failed":
          return <GenerationFailedCard onRetry={handlers.onRetryGeneration} />;
      }
  }
}
